import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Image as ImageIcon, Copy, Check, Trash2, Loader2, Filter, Tag, Square, CheckSquare, FileDown } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

import { API_BASE } from '../lib/api';
import { smartCompressImage, formatSize } from '../lib/imageCompression';

interface MediaItem {
  key: string;
  name: string;
  category: string;
  size: number;
  uploaded: string;
  url: string;
}

const MEDIA_CATEGORIES = [
  { value: 'all', label: '全部分類' },
  { value: 'product', label: '產品' },
  { value: 'media-coverage', label: '媒體報道' },
  { value: 'story', label: '品牌故事' },
  { value: 'logo', label: 'Logo' },
  { value: 'hero', label: 'Hero 背景' },
  { value: 'general', label: '一般' },
];

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  MEDIA_CATEGORIES.map(c => [c.value, c.label])
);

const AdminMediaLibrary: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [uploadCategory, setUploadCategory] = useState('general');
  // 上傳格式：WebP 預設（檔案較細）；WhatsApp 圖片訊息（優惠券/廣播）必須用 JPG
  const [uploadFormat, setUploadFormat] = useState<'webp' | 'jpeg'>('webp');
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('product');
  const [bulkUpdatingCategory, setBulkUpdatingCategory] = useState(false);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchMedia();
  }, [token, navigate, filterCategory]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterCategory && filterCategory !== 'all') {
        params.set('category', filterCategory);
      }
      const res = await fetch(`${API_BASE}/api/public/admin/media?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMediaList(data);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        setError('獲取媒體列表失敗');
      }
    } catch (e) {
      setError('獲取媒體列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const uploadSingleFile = async (file: File): Promise<{ ok: boolean; error?: string }> => {
    // Smart tiered compression; fallback to original on any failure (never blocks upload)
    const result = await smartCompressImage(file, { format: uploadFormat }).catch(() => null);
    const uploadFile = result?.file ?? file;
    if (result?.isCompressed) {
      console.log(`[MediaUpload] ${file.name}: ${formatSize(result.originalSize)} → ${formatSize(result.compressedSize)} (${result.ratio})`);
    }
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('category', uploadCategory);

    try {
      const res = await fetch(`${API_BASE}/api/public/admin/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
        return { ok: false, error: '登入已過期，請重新登入' };
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.detail || data.error || `HTTP ${res.status}` };
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : '網絡錯誤' };
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));
    if (invalidFiles.length > 0) {
      setError(`請選擇有效的圖片檔案（JPG、PNG、GIF、WEBP、SVG）。以下檔案不支援：${invalidFiles.map(f => f.name).join(', ')}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    let successCount = 0;
    let failCount = 0;
    let firstError = '';

    for (let i = 0; i < files.length; i++) {
      setUploadProgress(Math.round((i / files.length) * 100));
      const result = await uploadSingleFile(files[i]);
      if (result.ok) {
        successCount++;
      } else {
        failCount++;
        if (!firstError) firstError = result.error || '';
      }
    }

    setUploadProgress(100);
    await fetchMedia();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setTimeout(() => {
      setUploading(false);
      setUploadProgress(0);
      if (failCount > 0) {
        setError(`上傳完成：${successCount} 個成功，${failCount} 個失敗${firstError ? `（${firstError}）` : ''}`);
      }
    }, 500);
  };

  const handleCopyUrl = async (item: MediaItem) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopiedKey(item.key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      setError('複製 URL 失敗');
    }
  };

  const handleDelete = async (item: MediaItem) => {
    if (!confirm(`確定要刪除「${item.name}」嗎？此操作無法復原。`)) return;

    try {
      const res = await fetch(`${API_BASE}/api/public/admin/media/${encodeURIComponent(item.key)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMediaList(prev => prev.filter(m => m.key !== item.key));
        setSelectedKeys(prev => { const next = new Set(prev); next.delete(item.key); return next; });
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '刪除失敗');
      }
    } catch (e) {
      setError('刪除失敗');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedKeys.size === 0) return;
    if (!confirm(`確定要刪除選取的 ${selectedKeys.size} 個檔案嗎？此操作無法復原。`)) return;

    setBulkDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/media/bulk-delete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ keys: Array.from(selectedKeys) }),
      });
      if (res.ok) {
        const data = await res.json();
        setMediaList(prev => prev.filter(m => !selectedKeys.has(m.key)));
        setSelectedKeys(new Set());
        if (data.failed > 0) {
          setError(`批量刪除完成：${data.deleted} 個成功，${data.failed} 個失敗`);
        }
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '批量刪除失敗');
      }
    } catch (e) {
      setError('批量刪除失敗');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkUpdateCategory = async () => {
    if (selectedKeys.size === 0) return;
    if (!confirm(`確定將選取的 ${selectedKeys.size} 個檔案分類改為「${CATEGORY_LABELS[bulkCategory]}」嗎？`)) return;

    setBulkUpdatingCategory(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/media/bulk-update-category`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ keys: Array.from(selectedKeys), category: bulkCategory }),
      });
      if (res.ok) {
        const data = await res.json();
        setMediaList(prev => prev.map(m => selectedKeys.has(m.key) ? { ...m, category: bulkCategory } : m));
        setSelectedKeys(new Set());
        if (data.failed > 0) {
          setError(`批量改分類完成：${data.updated} 個成功，${data.failed} 個失敗`);
        }
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '批量改分類失敗');
      }
    } catch (e) {
      setError('批量改分類失敗');
    } finally {
      setBulkUpdatingCategory(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory && filterCategory !== 'all') {
        params.set('category', filterCategory);
      }
      const res = await fetch(`${API_BASE}/api/public/admin/media/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '匯出失敗');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `media-library-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('匯出失敗');
    }
  };

  const toggleSelection = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelectedKeys(new Set(mediaList.map(m => m.key)));
  const deselectAll = () => setSelectedKeys(new Set());

  const handleCategoryChange = async (item: MediaItem, newCategory: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/media/${encodeURIComponent(item.key)}/category`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ category: newCategory }),
      });
      if (res.ok) {
        setMediaList(prev => prev.map(m => m.key === item.key ? { ...m, category: newCategory } : m));
        setEditingCategory(null);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '更新分類失敗');
      }
    } catch (e) {
      setError('更新分類失敗');
    }
  };

  return (
    <AdminLayout currentPage="media-library">
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <Upload size={20} className="text-blue-600" />
            上傳圖片
          </h2>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <label className="flex-1 cursor-pointer w-full">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                disabled={uploading}
                className="hidden"
              />
              <div className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <ImageIcon size={24} className="text-gray-400 dark:text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {uploading ? '正在上傳...' : '點擊選擇一個或多個圖片檔案'}
                </span>
              </div>
            </label>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setUploadFormat('webp')}
                  disabled={uploading}
                  className={`px-3 py-2 transition ${uploadFormat === 'webp' ? 'bg-blue-600 text-white font-bold' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                >
                  WebP（預設）
                </button>
                <button
                  type="button"
                  onClick={() => setUploadFormat('jpeg')}
                  disabled={uploading}
                  className={`px-3 py-2 transition border-l border-gray-300 dark:border-gray-600 ${uploadFormat === 'jpeg' ? 'bg-blue-600 text-white font-bold' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  title="WhatsApp 圖片訊息（優惠券／廣播）必須用 JPG——WebP 會被當成 sticker，對方收唔到圖"
                >
                  JPG（WhatsApp 用）
                </button>
              </div>
              <Tag size={18} className="text-gray-500 dark:text-gray-400" />
              <select
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                disabled={uploading}
                className="flex-1 sm:w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
              >
                {MEDIA_CATEGORIES.filter(c => c.value !== 'all').map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {uploading && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300 mb-1">
                <span className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  上傳中...
                </span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-200"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Filter & Bulk Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={18} className="text-gray-500 dark:text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
            >
              {MEDIA_CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            {mediaList.length > 0 && (
              <>
                <button
                  onClick={selectAll}
                  className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <CheckSquare size={14} /> 全選
                </button>
                <button
                  onClick={deselectAll}
                  className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <Square size={14} /> 取消全選
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportCsv}
              className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <FileDown size={14} />
              匯出 CSV
            </button>
            {selectedKeys.size > 0 && (
              <>
                <div className="flex items-center gap-2">
                  <Tag size={14} className="text-gray-500 dark:text-gray-400" />
                  <select
                    value={bulkCategory}
                    onChange={(e) => setBulkCategory(e.target.value)}
                    disabled={bulkUpdatingCategory}
                    className="px-2 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    {MEDIA_CATEGORIES.filter(c => c.value !== 'all').map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleBulkUpdateCategory}
                    disabled={bulkUpdatingCategory}
                    className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {bulkUpdatingCategory ? '更新中...' : `改分類 (${selectedKeys.size})`}
                  </button>
                </div>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  {bulkDeleting ? '刪除中...' : `刪除所選 (${selectedKeys.size})`}
                </button>
              </>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400">
              共 <span className="font-medium text-gray-800 dark:text-white">{mediaList.length}</span> 個檔案
            </p>
          </div>
        </div>

        {/* Media Grid */}
        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
            <Loader2 size={24} className="animate-spin mx-auto mb-2" />
            加載中...
          </div>
        ) : mediaList.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
            <ImageIcon size={48} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>尚未上傳任何圖片</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {mediaList.map(item => (
              <div
                key={item.key}
                className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Thumbnail */}
                <div className="aspect-square bg-gray-50 dark:bg-gray-900 flex items-center justify-center overflow-hidden relative group">
                  <input
                    type="checkbox"
                    checked={selectedKeys.has(item.key)}
                    onChange={() => toggleSelection(item.key)}
                    className="absolute top-2 left-2 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 z-10 cursor-pointer"
                  />
                  <img
                    src={item.url}
                    alt={item.name}
                    className={`w-full h-full object-cover transition ${selectedKeys.has(item.key) ? 'opacity-60' : ''}`}
                    loading="lazy"
                    onError={e => {
                      (e.target as HTMLImageElement).src = '';
                      (e.target as HTMLImageElement).style.display = 'none';
                      const parent = (e.target as HTMLImageElement).parentElement;
                      if (parent) {
                        const fallback = document.createElement('div');
                        fallback.className = 'flex flex-col items-center justify-center text-gray-400 dark:text-gray-500';
                        fallback.innerHTML = `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg><span class="text-xs mt-1">無法顯示</span>`;
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="text-sm font-medium text-gray-800 dark:text-white truncate" title={item.name}>
                    {item.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {formatSize(item.size)} · {new Date(item.uploaded).toLocaleDateString('zh-HK')}
                  </p>

                  {/* Category */}
                  <div className="mt-2">
                    {editingCategory === item.key ? (
                      <select
                        autoFocus
                        value={item.category}
                        onChange={(e) => handleCategoryChange(item, e.target.value)}
                        onBlur={() => setEditingCategory(null)}
                        className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-700 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 outline-none"
                      >
                        {MEDIA_CATEGORIES.filter(c => c.value !== 'all').map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    ) : (
                      <button
                        onClick={() => setEditingCategory(item.key)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="點擊修改分類"
                      >
                        <Tag size={10} />
                        {CATEGORY_LABELS[item.category] || item.category}
                      </button>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleCopyUrl(item)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      {copiedKey === item.key ? (
                        <>
                          <Check size={14} />
                          已複製
                        </>
                      ) : (
                        <>
                          <Copy size={14} />
                          複製 URL
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <Trash2 size={14} />
                      刪除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMediaLibrary;
